import { useCallback, useEffect, useMemo, useRef } from 'react';

type UseRecordingTimerOptions = {
  durationSeconds: number;
  onDurationChange: (durationSeconds: number) => void;
  onResetDuration: () => void;
};

export const useRecordingTimer = ({
  durationSeconds,
  onDurationChange,
  onResetDuration
}: UseRecordingTimerOptions) => {
  const durationIntervalRef = useRef<NodeJS.Timeout>();
  const durationSecondsRef = useRef(durationSeconds);
  const totalElapsedRef = useRef(0);
  const lastStartRef = useRef(0);
  const lastChunkElapsedRef = useRef(0);

  useEffect(() => {
    durationSecondsRef.current = durationSeconds;
  }, [durationSeconds]);

  const getCurrentElapsedMs = useCallback(() => {
    if (!durationIntervalRef.current) return totalElapsedRef.current;

    return totalElapsedRef.current + (Date.now() - lastStartRef.current);
  }, []);

  const getChunkDurationSeconds = useCallback(() => {
    const chunkEndElapsed = getCurrentElapsedMs();
    const chunkDurationMs = Math.max(0, chunkEndElapsed - lastChunkElapsedRef.current);

    lastChunkElapsedRef.current = chunkEndElapsed;

    return chunkDurationMs / 1000;
  }, [getCurrentElapsedMs]);

  const updateDuration = useCallback(() => {
    const newDuration = totalElapsedRef.current + (Date.now() - lastStartRef.current);
    onDurationChange(newDuration / 1000);
  }, [onDurationChange]);

  const stop = useCallback(
    ({ commitElapsed = false } = {}) => {
      if (!durationIntervalRef.current) return;

      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = undefined;

      if (commitElapsed) {
        const now = Date.now();
        totalElapsedRef.current += now - lastStartRef.current;
        lastStartRef.current = now;
        onDurationChange(totalElapsedRef.current / 1000);
      }
    },
    [onDurationChange]
  );

  const start = useCallback(
    ({ resetDuration = false } = {}) => {
      stop();

      if (resetDuration) {
        onResetDuration();
        totalElapsedRef.current = 0;
      } else {
        totalElapsedRef.current = durationSecondsRef.current * 1000;
      }

      lastChunkElapsedRef.current = totalElapsedRef.current;
      lastStartRef.current = Date.now();
      durationIntervalRef.current = setInterval(updateDuration, 100);
    },
    [onResetDuration, stop, updateDuration]
  );

  const reset = useCallback(() => {
    stop();
    onResetDuration();
    totalElapsedRef.current = 0;
    lastStartRef.current = 0;
    lastChunkElapsedRef.current = 0;
  }, [onResetDuration, stop]);

  return useMemo(
    () => ({
      start,
      stop,
      reset,
      getCurrentElapsedMs,
      getChunkDurationSeconds
    }),
    [getChunkDurationSeconds, getCurrentElapsedMs, reset, start, stop]
  );
};
